/**
    * Divider tool mixin for Vue.js
    * Provides functionality for creating and managing divider lines between panels
 */
export default {
    data() {
        return {
            divider_line: null,
            divider_start_point: null,
            is_drawing_divider: false,            
            divider_mouseup_handler: null,
            divider_width: 20,
            divider_animation_frame: null, // For tracking requestAnimationFrame
            last_mouse_pos: null,
        };
    },
    
    methods: {
        
        toggle_divider() {
            if (!this.selected_panel){
                console.log('No panel selected, cannot toggle divider tool');
                return;
            }

            // Specifically check if the panel is NOT global (fixed comparison)
            if (this.selected_panel.getAttr('is_global') === true) {
                console.log('Global panel cannot be divided');
                return;
            }

            const divider_enabled = this.active_subtool === 'divider';
            
            if (divider_enabled) {
                this.deselect_subtool();
                this.cancel_divider_drawing();
            } else {

                // if (this.active_tool == 'select') {
                //     tool_handlers.select.deactivate(this); //call deactivate
                // }      

                this.change_tool("select_panel",false);               
                this.change_subtool("divider");
                this.setup_divider_events();                
            }
        },
        
        setup_divider_events() {
            if (!this.stage || !this.selected_panel){
                this.deselect_subtool();
                return;
            }
            
            // First remove any previous listeners
            this.stage.off('mousedown.divider touchstart.divider');
            this.stage.off('mousemove.divider touchmove.divider');
            this.stage.off('mouseup.divider touchend.divider');
            window.removeEventListener('mouseup', this.divider_mouseup_handler);
            
            // Add event listeners for drawing the divider line
            this.stage.on('mousedown.divider touchstart.divider', (e) => {
                if (!this.selected_panel || this.active_subtool !== 'divider') return;
                
                // Use adjusted coordinates that account for zoom and pan
                const adjusted_pos = this.get_pointer_position();
                if (!adjusted_pos) return;
                
                // Start drawing from anywhere - we'll check for panel intersection later
                this.is_drawing_divider = true;
                
                // Store global start position with adjusted coordinates 
                this.divider_start_point = {
                    x: adjusted_pos.x,
                    y: adjusted_pos.y
                };
                
                // Create the divider line in adjusted coordinates
                this.divider_line = new Konva.Line({
                    points: [adjusted_pos.x, adjusted_pos.y, adjusted_pos.x, adjusted_pos.y],
                    stroke: '#0096ff',
                    strokeWidth: 2 / this.canvas_zoom, // Adjust stroke width for zoom
                    dash: [5 / this.canvas_zoom, 5 / this.canvas_zoom], // Adjust dash for zoom
                    name: 'divider_line'
                });
                
                this.ui_layer.add(this.divider_line);
                this.redraw();

                // Add RAF handler for mouse movement
                this.divider_animation_frame = null;
                this.last_mouse_pos = null;
            });
            
            // Move the line while dragging - using RAF for performance
            this.stage.on('mousemove.divider touchmove.divider', (e) => {
                if (!this.is_drawing_divider || !this.divider_line) return;

                // Get pointer position
                let pos = this.get_pointer_position();
                if (!pos) return;

                // Detect if shift is pressed (for both mouse and touch events)
                let shift_pressed = false;
                if (e && e.evt && typeof e.evt.shiftKey === 'boolean') {
                    shift_pressed = e.evt.shiftKey;
                }

                // If shift is pressed, force horizontal or vertical
                if (shift_pressed && this.divider_start_point) {
                    const dx = Math.abs(pos.x - this.divider_start_point.x);
                    const dy = Math.abs(pos.y - this.divider_start_point.y);
                    if (dx > dy) {
                        pos.y = this.divider_start_point.y;
                    } else {
                        pos.x = this.divider_start_point.x;
                    }
                }

                // Store the current mouse position
                this.last_mouse_pos = pos;

                // Use RAF to limit updates
                if (!this.divider_animation_frame) {
                    this.divider_animation_frame = requestAnimationFrame(() => {
                        // Update line only if we have a valid position
                        if (this.last_mouse_pos) {
                            const points = this.divider_line.points();
                            points[2] = this.last_mouse_pos.x;
                            points[3] = this.last_mouse_pos.y;
                            this.divider_line.points(points);
                            this.redraw();
                        }
                        this.divider_animation_frame = null;
                    });
                }
            });
            
            // Finalize drawing the line when mouse is released on stage
            this.stage.on('mouseup.divider touchend.divider', (e) => {
                if (!this.is_drawing_divider || !this.divider_line) return;
                // Cancel any pending animation frame
                if (this.divider_animation_frame) {
                    cancelAnimationFrame(this.divider_animation_frame);
                    this.divider_animation_frame = null;
                }
                this.finalize_divider(e);
            });

            // Add global listener for mouseup in case pointer is released outside stage
            this.divider_mouseup_handler = (e) => {
                if (this.is_drawing_divider) {
                    // Cancel any pending animation frame
                    if (this.divider_animation_frame) {
                        cancelAnimationFrame(this.divider_animation_frame);
                        this.divider_animation_frame = null;
                    }
                    this.finalize_divider(e);
                }
            };
            window.addEventListener('mouseup', this.divider_mouseup_handler);
        },
        
        finalize_divider(e) {
            // Use adjusted coordinates
            let adjusted_pos = this.get_pointer_position();
            if (!adjusted_pos) {
                this.cancel_divider_drawing();
                return;
            }
            
            // Apply shift constraint if shift is pressed (same logic as in mousemove)
            let shift_pressed = false;
            if (e && e.evt && typeof e.evt.shiftKey === 'boolean') {
                shift_pressed = e.evt.shiftKey;
            }
            
            // If shift is pressed, force horizontal or vertical
            if (shift_pressed && this.divider_start_point) {
                const dx = Math.abs(adjusted_pos.x - this.divider_start_point.x);
                const dy = Math.abs(adjusted_pos.y - this.divider_start_point.y);
                if (dx > dy) {
                    adjusted_pos.y = this.divider_start_point.y;
                } else {
                    adjusted_pos.x = this.divider_start_point.x;
                }
            }
            
            // Get panel position and dimensions
            const panel_pos = {
                x: this.selected_panel.x(),
                y: this.selected_panel.y()
            };
            
            const panel_points = this.selected_panel.points;
            const bounds = this.calculate_polygon_bounds(panel_points);
            const panel_width = bounds.width;
            const panel_height = bounds.height;
            
            // Check if line intersects with panel using adjusted coordinates
            const line_start = this.divider_start_point;
            const line_end = {x: adjusted_pos.x, y: adjusted_pos.y};
            
            const intersects = this.line_intersects_panel(line_start, line_end, panel_pos, panel_width, panel_height);
            
            if (!intersects) {
                this.cancel_divider_drawing();
                return;
            }
            
            // Calculate line properties
            const dx = line_end.x - line_start.x;
            const dy = line_end.y - line_start.y;
            const line_length = Math.sqrt(dx * dx + dy * dy);
            
            // If the line is too short, cancel
            if (line_length < 20) {
                this.cancel_divider_drawing();
                return;
            }
            
            // Convert global coordinates to panel-local coordinates
            const start_local = {
                x: line_start.x - panel_pos.x,
                y: line_start.y - panel_pos.y
            };
            const end_local = {
                x: line_end.x - panel_pos.x,
                y: line_end.y - panel_pos.y
            };
            
            // Execute the division based on the drawn line
            this.execute_panel_division(start_local, end_local);
            
            // Cleanup
            this.cancel_divider_drawing();
        },
        
        cancel_divider_drawing() {
            this.is_drawing_divider = false;
            this.divider_start_point = null;
            
            // Cancel any pending animation frame
            if (this.divider_animation_frame) {
                cancelAnimationFrame(this.divider_animation_frame);
                this.divider_animation_frame = null;
            }
            
            if (this.divider_line) {
                this.divider_line.destroy();
                this.divider_line = null;
                this.redraw();
            }
            
            // Remove event listeners
            this.stage.off('mousedown.divider touchstart.divider');
            this.stage.off('mousemove.divider touchmove.divider');
            this.stage.off('mouseup.divider touchend.divider');
            if(this.divider_mouseup_handler) {
                window.removeEventListener('mouseup', this.divider_mouseup_handler);
                this.divider_mouseup_handler = null;
            }
        },
        
        line_intersects_panel(line_start, line_end, panel_pos, panel_width, panel_height) {

            // Get the actual polygon points
            const panel_points = this.selected_panel.points;
            const panel_edges = [];
            
            // Create real edges of the polygon in global coordinates
            for (let i = 0; i < panel_points.length; i += 2) {
                const current_i = i;
                const next_i = (i + 2) % panel_points.length;
                
                panel_edges.push({
                    x1: panel_pos.x + panel_points[current_i],
                    y1: panel_pos.y + panel_points[current_i + 1],
                    x2: panel_pos.x + panel_points[next_i],
                    y2: panel_pos.y + panel_points[next_i + 1]
                });
            }
            

            //console.log(`Real panel edges: ${JSON.stringify(panel_edges)}`);

            // Check if the drawn line intersects any panel edge
            for (const edge of panel_edges) {
                if (this.lines_intersect(
                    line_start.x, line_start.y, line_end.x, line_end.y,
                    edge.x1, edge.y1, edge.x2, edge.y2
                )) {
                    return true;
                }
            }
            
            // Also check if either endpoint is inside the panel
            const start_in_panel = this.point_in_panel(line_start, panel_pos, panel_width, panel_height);
            const end_in_panel = this.point_in_panel(line_end, panel_pos, panel_width, panel_height);
            
            //console.log(`Start in panel: ${start_in_panel}, End in panel: ${end_in_panel}`);

            return start_in_panel || end_in_panel;
        },
        
        lines_intersect(x1, y1, x2, y2, x3, y3, x4, y4) {
            // Calculate the direction vectors
            const dx1 = x2 - x1;
            const dy1 = y2 - y1;
            const dx2 = x4 - x3;
            const dy2 = y4 - y3;
            
            // Calculate the determinant
            const determinant = dx1 * dy2 - dy1 * dx2;
            
            // If determinant is zero, lines are parallel
            if (determinant === 0) return false;
            
            // Calculate the parameters t and s
            const dx3 = x1 - x3;
            const dy3 = y1 - y3;
            const t = (dx2 * dy3 - dy2 * dx3) / determinant;
            const s = (dx1 * dy3 - dy1 * dx3) / determinant;
            
            // Check if the intersection point lies on both line segments
            return t >= 0 && t <= 1 && s >= 0 && s <= 1;
        },
        
        point_in_panel(point, panel_pos, panel_width, panel_height) {
            return point.x >= panel_pos.x && point.x <= panel_pos.x + panel_width &&
                   point.y >= panel_pos.y && point.y <= panel_pos.y + panel_height;
        },       
        
        split_polygon_by_line(polygon, line_start, line_end) {
            // Convertir el polígono a array de objetos punto
            const polygon_points = [];
            for (let i = 0; i < polygon.length; i += 2) {
                polygon_points.push({ x: polygon[i], y: polygon[i + 1] });
            }
            
            // Vector director de la línea
            const dx = line_end.x - line_start.x;
            const dy = line_end.y - line_start.y;
            const line_length = Math.sqrt(dx * dx + dy * dy);
            
            // Vector perpendicular normalizado
            const perp_x = -dy / line_length;
            const perp_y = dx / line_length;
            
            // Offset del espacio del divisor (mitad a cada lado)
            const half_gap = parseInt(this.divider_width, 10) / 2 || 0;
            
            // Crear líneas desplazadas para cada lado del divisor
            const line1_start = { x: line_start.x + perp_x * half_gap, y: line_start.y + perp_y * half_gap };
            const line1_end = { x: line_end.x + perp_x * half_gap, y: line_end.y + perp_y * half_gap };
            
            const line2_start = { x: line_start.x - perp_x * half_gap, y: line_start.y - perp_y * half_gap };
            const line2_end = { x: line_end.x - perp_x * half_gap, y: line_end.y - perp_y * half_gap };
            
            // Dividir el polígono con cada línea desplazada
            const poly1 = [];
            const poly2 = [];
            
            // Para la primera línea (desplazada en dirección positiva)
            for (let i = 0; i < polygon_points.length; i++) {
                const current = polygon_points[i];
                const next = polygon_points[(i + 1) % polygon_points.length];
                const current_side = this.point_side_of_line(current, line1_start, line1_end);
                
                // Añadir el vértice actual al polígono correspondiente
                if (current_side >= 0) {
                    poly1.push(current);
                }
                
                // Comprobar si el borde cruza la línea de corte
                const next_side = this.point_side_of_line(next, line1_start, line1_end);
                if (current_side * next_side < 0) {
                    const intersection = this.compute_line_intersection(current, next, line1_start, line1_end);
                    if (intersection) {
                        poly1.push(intersection);
                    }
                }
            }
            
            // Para la segunda línea (desplazada en dirección negativa)
            for (let i = 0; i < polygon_points.length; i++) {
                const current = polygon_points[i];
                const next = polygon_points[(i + 1) % polygon_points.length];
                const current_side = this.point_side_of_line(current, line2_start, line2_end);
                
                // Añadir el vértice actual al polígono correspondiente
                if (current_side < 0) {
                    poly2.push(current);
                }
                
                // Comprobar si el borde cruza la línea de corte
                const next_side = this.point_side_of_line(next, line2_start, line2_end);
                if (current_side * next_side < 0) {
                    const intersection = this.compute_line_intersection(current, next, line2_start, line2_end);
                    if (intersection) {
                        poly2.push(intersection);
                    }
                }
            }
            
            // Convertir arrays de puntos a arrays de números
            function flatten_poly(poly) {
                const arr = [];
                for (const pt of poly) {
                    arr.push(pt.x, pt.y);
                }
                return arr;
            }
            
            const flat_poly1 = flatten_poly(poly1);
            const flat_poly2 = flatten_poly(poly2);
            
            // Calcular dimensiones para validar el tamaño mínimo
            const min_dimension = 80; // Tamaño mínimo razonable
            
            // Calcular dimensiones para ambos polígonos
            const bounds1 = this.calculate_polygon_bounds(flat_poly1);
            const bounds2 = this.calculate_polygon_bounds(flat_poly2);
            
            // Validar que ambos polígonos tengan un tamaño mínimo
            if (bounds1.width < min_dimension || bounds1.height < min_dimension || 
                bounds2.width < min_dimension || bounds2.height < min_dimension) {
                console.log("Panel so small to be valid, cancelling division");
                return null;
            }
            
            return [flat_poly1, flat_poly2];
        },

        point_side_of_line(point, line_start, line_end) {
            // Calculate the cross product between the line vector and the vector from line_start to point
            // Returns positive if point is on one side, negative if on the other side, and zero if on the line
            return (line_end.x - line_start.x) * (point.y - line_start.y) - 
                (line_end.y - line_start.y) * (point.x - line_start.x);
        },

        compute_line_intersection(p1, p2, p3, p4) {
            const denominator = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
            if (Math.abs(denominator) < 0.00001) {
                return null; // parallel lines
            }
            const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / denominator;
            return { x: p1.x + t * (p2.x - p1.x), y: p1.y + t * (p2.y - p1.y) };
        },

        execute_panel_division(start_point, end_point) {   
            if (!this.selected_panel) return;
            
            const panel_points = this.selected_panel.points;
            
            // Usar calculate_polygon_bounds en lugar de índices fijos
            const bounds = this.calculate_polygon_bounds(panel_points);
            const panel_width = bounds.width;
            const panel_height = bounds.height;
            
            if (panel_width < 80 || panel_height < 80) {
                console.log("panel too small to divide");
                return;
            }
           
            // Dividir el polígono actual con la línea dibujada
            const polygons = this.split_polygon_by_line(panel_points, start_point, end_point);
            
            // Validar que la división fue exitosa y produjo dos polígonos
            if (!polygons || polygons.length !== 2) {
                //show warning popup
                const html_content = `
                <div class="mb-6">
                    <h3 class="text-lg font-medium text-white mb-2">${this.get_t('help.divider_tool')}</h3>
                    <ul class="space-y-2">
                        <li class="flex items-start gap-3">
                            <i data-lucide="scissors" class="w-5 h-5 mt-0.5 text-rose-400"></i>
                            <div>
                                The line drawn does not intersect the panel or the resulting panels are too small.
                            </div>
                        </li>
                    </ul>
                </div>
                `;

                this.show_popup_with_content('The panel split failed.', html_content, { type: 'warning' });
                this.deselect_subtool();
                this.restore_previous_tool();

                return;
            }
            
            const polygon1 = polygons[0];
            const polygon2 = polygons[1];
            
            // Crear los paneles divididos
            this.create_divided_panels(polygon1, polygon2);
        },

        create_divided_panels(polygon1, polygon2) {
            // Check premium limits for panel creation (count only content panels, not global)
            const content_panels = this.panels.filter(panel => !panel.getAttr('is_global'));
            if (!this.check_local_restriction('max_panels_per_page', content_panels.length)) {
                // Reset divider tool state when validation fails
                this.deselect_subtool();
                this.is_dividing = false;
                this.selected_panel = null;
                this.deselect_all();
                this.redraw();
                return;
            }
            
            // Store original panel for reference
            const original_panel = this.selected_panel;
            
            // Create first panel
            const panel1 = new Konva.Group({
                x: original_panel.x(),
                y: original_panel.y(),
                name: 'panel_group',
                id: 'panel_' + Date.now() + '_1'
            });
            
            // Add panel attributes
            panel1.setAttr('is_panel', true);
            panel1.setAttr('respect_margins', true); // Default to respecting margins
            
            const background1 = new Konva.Line({
                points: polygon1,
                closed: true,
                fill: original_panel.background.fill(),
                name: 'panel_background'
            });
            
            panel1.add(background1);
            
            const content_group1 = new Konva.Group({
                name: 'content_group',
                listening: true
            });
            
            content_group1.clipFunc(function(ctx) {
                ctx.beginPath();
                for (let i = 0; i < polygon1.length; i += 2) {
                    if (i === 0) ctx.moveTo(polygon1[i], polygon1[i + 1]);
                    else ctx.lineTo(polygon1[i], polygon1[i + 1]);
                }
                ctx.closePath();
            });
            
            panel1.add(content_group1);
            
            const outline1 = new Konva.Line({
                points: polygon1,
                closed: true,
                stroke: this.outline_color, // Use current outline color
                strokeWidth: this.outline_width,
                fill: null,
                name: 'panel_outline',
                listening: false // Critical: outline should not capture events
            });
            
            panel1.add(outline1);
            panel1.outline = outline1;
            panel1.background = background1;
            panel1.content_group = content_group1;
            panel1.points = polygon1;
            
            // Create second panel similarly
            const panel2 = new Konva.Group({
                x: original_panel.x(),
                y: original_panel.y(),
                name: 'panel_group',
                id: 'panel_' + Date.now() + '_2'
            });
            
            // Add panel attributes
            panel2.setAttr('is_panel', true);
            panel2.setAttr('respect_margins', true); // Default to respecting margins
            
            const background2 = new Konva.Line({
                points: polygon2,
                closed: true,
                fill: original_panel.background.fill(),
                name: 'panel_background'
            });
            
            panel2.add(background2);
            
            const content_group2 = new Konva.Group({
                name: 'content_group',
                listening: true
            });
            
            content_group2.clipFunc(function(ctx) {
                ctx.beginPath();
                for (let i = 0; i < polygon2.length; i += 2) {
                    if (i === 0) ctx.moveTo(polygon2[i], polygon2[i + 1]);
                    else ctx.lineTo(polygon2[i], polygon2[i + 1]);
                }
                ctx.closePath();
            });
            
            panel2.add(content_group2);
            
            const outline2 = new Konva.Line({
                points: polygon2,
                closed: true,
                stroke: this.outline_color, // Use current outline color
                strokeWidth: this.outline_width,
                fill: null,
                name: 'panel_outline',
                listening: false // Critical: outline should not capture events
            });
            
            panel2.add(outline2);
            panel2.outline = outline2;
            panel2.background = background2;
            panel2.content_group = content_group2;
            panel2.points = polygon2;
            
            // Distribute images to appropriate panels
            const images = original_panel.content_group.find('.panel_image');
            
            images.forEach(img => {
                // Get image position relative to original panel
                const img_pos = {
                    x: img.x(),
                    y: img.y()
                };
                
                // Check which panel the image belongs to
                if (this.is_point_in_polygon(img_pos, polygon1)) {
                    const img_clone = img.clone();
                    content_group1.add(img_clone);
                } else if (this.is_point_in_polygon(img_pos, polygon2)) {
                    const img_clone = img.clone();
                    content_group2.add(img_clone);
                }
            });
            
            // Remove original panel from panels array
            const panel_index = this.panels.indexOf(original_panel);
            if (panel_index !== -1) {
                this.panels.splice(panel_index, 1);
            }
            
            // Remove original panel from stage
            original_panel.destroy();
            
            // Add new panels
            this.panels_layer.add(panel1);
            this.panels_layer.add(panel2);
            this.panels.push(panel1);
            this.panels.push(panel2);
            
            // Deselect panel
            this.selected_panel = null;
            
            // Redraw
            this.redraw();
            
            // Record in history
            this.push_to_history('divide_panel');
            this.deselect_subtool();
        },
        
        is_point_in_polygon(point, polygon_points) {
            let inside = false;
            for (let i = 0, j = polygon_points.length - 2; i < polygon_points.length; i += 2) {
                const xi = polygon_points[i];
                const yi = polygon_points[i + 1];
                const xj = polygon_points[j];
                const yj = polygon_points[j + 1];
                
                const intersect = ((yi > point.y) !== (yj > point.y)) &&
                    (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
                
                if (intersect) inside = !inside;
                j = i;
            }
            
            return inside;
        },
       
        update_divider_width(width) {
            this.divider_width = parseInt(width, 10);
        },       


    }
};